import { useEffect, useState, useCallback } from 'react';
import { track, identify } from '../lib/track';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, PackageCheck } from 'lucide-react';
import { supabase } from '../supabaseClient';
import HoldNotice from '../components/HoldNotice';
import { useCart } from '../context/CartContext';
import { clearPendingOrder } from '../lib/pendingOrder';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

const PAYMENT_LABEL = {
  pending: 'Payment processing…',
  // Deliberately not the word "paid". The customer has not paid at this point
  // and telling them they have is the one thing that would make the pending
  // line on their statement genuinely confusing.
  authorized: 'Card held — not yet charged',
  paid: 'Paid',
  expired: 'Checkout expired — nothing was charged',
  refunded: 'Refunded',
  canceled: 'Hold released — you were never charged',
  failed: 'Payment failed',
};

const FULFILLMENT_LABEL = {
  unfulfilled: 'Preparing your order',
  label_purchased: 'Label purchased — shipping soon',
  shipped: 'Shipped',
  ready_for_pickup: 'Ready for pickup',
  picked_up: 'Picked up',
  completed: 'Completed',
};

export default function OrderConfirmationPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { clearCart } = useCart();

  const load = useCallback(async () => {
    const { data, error: err } = await supabase.rpc('get_order_by_token', { p_token: token });
    if (err || !data) { setError('Order not found.'); setLoading(false); return; }
    setOrder(data);
    setLoading(false);

    // The order closes the funnel, and the email backfills identity onto the
    // whole session, including everything they did before checkout. That is
    // what turns "a customer says it broke" into a trail you can open.
    track('order_placed', {
      label: data.order_number ?? null,
      order_id: data.id ?? null,
      value_cents: data.total_cents ?? null,
      meta: { fulfillment: data.fulfillment ?? null, payment_status: data.payment_status ?? null },
    });
    if (data.email) identify(data.email);

    // This is the only safe moment to empty the cart: the order is real and
    // landed. Clearing it on the way out to Stripe (as this used to) meant an
    // abandoned checkout came back to an empty cart with the stock still held.
    if (['authorized', 'paid'].includes(data.payment_status)) {
      clearCart();
      clearPendingOrder();
    }
    // Dead ends released their own stock, so stop pointing at them.
    if (['expired', 'canceled', 'failed'].includes(data.payment_status)) {
      clearPendingOrder();
    }
  }, [token, clearCart]);

  useEffect(() => {
    // Eager best-effort confirm on the return trip from Stripe — the
    // shinyvault-stripe-webhook function is the durable source of truth, so
    // this is just here to make the confirmation page feel instant instead
    // of showing "processing" for a few seconds while the webhook lands.
    const confirmThenLoad = async () => {
      if (sessionId) {
        try {
          await supabase.functions.invoke('shinyvault-checkout', {
            body: { action: 'confirm_session', session_id: sessionId },
          });
        } catch { /* webhook will still catch this */ }
      }
      load();
    };
    confirmThenLoad();
  }, [sessionId, load]);

  // If payment is still showing pending shortly after returning from Stripe,
  // poll a couple times in case the webhook is a beat behind.
  useEffect(() => {
    if (!sessionId || !order || order.payment_status !== 'pending') return;
    const t = setTimeout(load, 2500);
    return () => clearTimeout(t);
  }, [sessionId, order, load]);

  if (loading) return <div className="sv-container" style={{ padding: 80 }}>Loading…</div>;
  if (error || !order) {
    return (
      <div className="sv-container sv-page-top sv-page-bottom" style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 10 }}>{error || 'Order not found'}</h1>
        <Link to="/order/lookup" className="sv-btn-outline" style={{ display: 'inline-flex', padding: '10px 18px' }}>
          Look up an order
        </Link>
      </div>
    );
  }

  const isPending = order.payment_status === 'pending';
  const isHeld = order.payment_status === 'authorized';
  // Expired, released or failed. The heading used to be a plain pending/success
  // toggle, so one of these rendered a green tick and "We've got your order" on
  // an order that does not exist any more — the single most likely way to make
  // somebody order a second time.
  const isDeadEnd = ['expired', 'canceled', 'failed'].includes(order.payment_status);
  const isRefunded = order.payment_status === 'refunded';

  const headline = isPending ? 'Confirming your order…'
    : isDeadEnd ? 'This order did not go through'
    : isRefunded ? 'This order was refunded'
    : "We've got your order";

  const headIcon = isPending ? <Clock size={26} color="var(--accent)" />
    : isDeadEnd || isRefunded ? <AlertTriangle size={26} color="var(--warning)" />
    : <CheckCircle2 size={26} color="var(--success)" />;

  return (
    <div className="sv-container sv-page-top sv-page-bottom" style={{ maxWidth: 620 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        {headIcon}
        <h1 style={{ fontSize: '1.5rem', fontWeight: 900 }}>{headline}</h1>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 24 }}>
        Order #{order.id.slice(0, 8).toUpperCase()} · {PAYMENT_LABEL[order.payment_status] || order.payment_status}
      </p>

      {/* Says the three things somebody in this state actually needs: were you
          charged, do you own it, and what do you do now. Vagueness here is what
          makes people order twice. */}
      {(isDeadEnd || isRefunded) && (
        <div
          style={{
            border: '1px solid var(--warning)', borderRadius: 12,
            padding: '16px 18px', marginBottom: 22, fontSize: '0.9rem', lineHeight: 1.65,
          }}
        >
          {isRefunded ? (
            <>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>You have been refunded in full.</div>
              The {money(order.total_cents)} for this order has been returned to the card you paid
              with. Depending on your bank it can take a few business days to appear.
              <strong> You do not owe anything and there is nothing to cancel.</strong> These items
              are not on their way to you — if you still want them, they need to be ordered again.
            </>
          ) : (
            <>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>
                You were not charged, and nothing is being shipped.
              </div>
              {order.payment_status === 'expired'
                ? 'This checkout was not completed in time, so the items were released and put back on the store for other customers.'
                : order.payment_status === 'canceled'
                  ? 'The hold on your card was released before any payment was taken, so no money ever left your account.'
                  : 'The payment did not complete, so the order was never placed.'}
              {' '}
              If your bank showed a pending amount for this, it drops off by itself — there is no
              refund to wait on because no payment was ever taken.
              <div style={{ marginTop: 12 }}>
                <strong>This order is finished and cannot be paid for.</strong> If you still want
                these items, start a fresh order. Check the store first, though — anything that was
                one of a kind may have gone to someone else.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <Link to="/shop" className="sv-btn" style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
                  Back to the store
                </Link>
              </div>
            </>
          )}
        </div>
      )}

      {isHeld && (
        <div style={{ marginBottom: 16 }}>
          <HoldNotice amountLabel={money(order.total_cents)} />
        </div>
      )}

      <div className="sv-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {(order.items || []).map((it) => (
            <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
              <span>{it.quantity}× {it.product_name_snapshot}</span>
              <span>{money(it.price_cents_snapshot * it.quantity)}</span>
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 12, paddingTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            <span>Subtotal</span><span>{money(order.subtotal_cents)}</span>
          </div>
          {order.shipping_rate_cents > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
              <span>Shipping</span><span>{money(order.shipping_rate_cents)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '1.05rem', marginTop: 6 }}>
            <span>Total</span><span>{money(order.total_cents)}</span>
          </div>
        </div>
      </div>

      {/* Hidden once an order is refunded or dead. A tracking number on a
          refunded order reads as "it is on its way" — and on a refunded label
          it is not, because the postage was voided too. */}
      {!isDeadEnd && !isRefunded && (
      <div className="sv-card" style={{ padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <PackageCheck size={18} color="var(--accent)" />
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
            {order.fulfillment_method === 'ship' ? 'Shipping' : 'Local pickup'}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {FULFILLMENT_LABEL[order.fulfillment_status] || order.fulfillment_status}
            {order.tracking_number && (
              <>
                {' · Tracking: '}
                {order.tracking_url
                  ? <a href={order.tracking_url} target="_blank" rel="noopener noreferrer">{order.tracking_number}</a>
                  : order.tracking_number}
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {order.receipt_url && (
        <a href={order.receipt_url} target="_blank" rel="noreferrer" className="sv-btn-outline" style={{ display: 'inline-flex', padding: '10px 18px' }}>
          View receipt
        </a>
      )}
    </div>
  );
}
